import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Shield, CheckCircle, XCircle, Clock, FileText,
  ToggleLeft, ToggleRight, Search, AlertCircle, Link,
} from 'lucide-react'
import { Card, Badge, PageHeader } from '../components/Card'
import {
  listPolicies, listApprovals, auditLog, togglePolicy,
  approveRequest, rejectRequest, listConnectedAccounts,
  type Policy, type Approval, type AuditEntry, type ConnectedAccount,
} from '../api/client'
import { layout } from '../utils/layout'

type Tab = 'policies' | 'approvals' | 'audit' | 'credentials'

function policyTypeColor(t: string): 'blue' | 'purple' | 'yellow' | 'slate' {
  if (t === 'content') return 'blue'
  if (t === 'spend') return 'purple'
  if (t === 'safety') return 'yellow'
  return 'slate'
}

function approvalStatusColor(s: string): 'yellow' | 'green' | 'red' | 'slate' {
  if (s === 'pending') return 'yellow'
  if (s === 'approved') return 'green'
  if (s === 'rejected') return 'red'
  return 'slate'
}

// ── Policies Tab ─────────────────────────────────────────────────────────────

function PoliciesTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['policies'], queryFn: listPolicies })

  const toggleMut = useMutation({
    mutationFn: (id: string) => togglePolicy(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  })

  if (isLoading) return <div className="py-8 text-center text-slate-500 text-sm">Loading policies…</div>

  return (
    <div className="space-y-3">
      {!data?.policies.length ? (
        <div className="py-8 text-center text-slate-500 text-sm">
          <Shield className="w-12 h-12 mx-auto mb-3 text-slate-700" />
          No policies configured yet.
        </div>
      ) : (
        data.policies.map((p: Policy) => (
          <div key={p.id} className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-slate-800/40 rounded-xl border border-surface-border">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-slate-200">{p.name}</span>
                <Badge color={policyTypeColor(p.policy_type)}>{p.policy_type}</Badge>
              </div>
              <p className="text-xs text-slate-500">
                Created {new Date(p.created_at).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={() => toggleMut.mutate(p.id)}
              disabled={toggleMut.isPending}
              className="shrink-0 transition-colors"
              title={p.enabled ? 'Disable policy' : 'Enable policy'}
            >
              {p.enabled ? (
                <ToggleRight className="w-8 h-8 text-green-400 hover:text-green-300" />
              ) : (
                <ToggleLeft className="w-8 h-8 text-slate-600 hover:text-slate-400" />
              )}
            </button>
            <Badge color={p.enabled ? 'green' : 'slate'}>{p.enabled ? 'Active' : 'Disabled'}</Badge>
          </div>
        ))
      )}
    </div>
  )
}

// ── Approvals Tab ─────────────────────────────────────────────────────────────

function ApprovalsTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['approvals'], queryFn: listApprovals })

  const approveMut = useMutation({
    mutationFn: (id: string) => approveRequest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  })

  const rejectMut = useMutation({
    mutationFn: (id: string) => rejectRequest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  })

  if (isLoading) return <div className="py-8 text-center text-slate-500 text-sm">Loading approvals…</div>

  const pending = data?.approvals.filter((a: Approval) => a.status === 'pending') ?? []
  const others = data?.approvals.filter((a: Approval) => a.status !== 'pending') ?? []

  return (
    <div className="space-y-4">
      {pending.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-semibold text-yellow-400">{pending.length} Pending Approval{pending.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-2">
            {pending.map((a: Approval) => (
              <div key={a.id} className="p-4 bg-yellow-900/10 border border-yellow-900/30 rounded-xl">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-slate-200">
                        {a.item_type.replace(/_/g, ' ')}
                      </span>
                      <Badge color="yellow">pending</Badge>
                    </div>
                    <p className="text-xs text-slate-500">
                      Requested by <span className="text-slate-400">{a.requester}</span>
                      {' · '}Item ID: <code className="text-slate-500">{a.item_id.slice(0, 8)}…</code>
                    </p>
                  </div>
                  <div className="flex gap-2 sm:shrink-0">
                    <button
                      onClick={() => rejectMut.mutate(a.id)}
                      disabled={rejectMut.isPending || approveMut.isPending}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1 text-xs bg-red-700/40 hover:bg-red-700/60 text-red-300 px-3 py-1.5 rounded-lg"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                    <button
                      onClick={() => approveMut.mutate(a.id)}
                      disabled={approveMut.isPending || rejectMut.isPending}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1 text-xs bg-green-700/40 hover:bg-green-700/60 text-green-300 px-3 py-1.5 rounded-lg"
                    >
                      <CheckCircle className="w-3.5 h-3.5" /> Approve
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {others.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-3 uppercase tracking-wide">Previous Requests</p>
          <div className="space-y-2">
            {others.slice(0, 10).map((a: Approval) => (
              <div key={a.id} className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg">
                <Badge color={approvalStatusColor(a.status)}>{a.status}</Badge>
                <span className="text-sm text-slate-400 capitalize truncate min-w-0 flex-1">{a.item_type.replace(/_/g, ' ')}</span>
                <span className="text-xs text-slate-600 shrink-0 hidden sm:block">{a.requester}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!data?.approvals.length && (
        <div className="py-8 text-center text-slate-500 text-sm">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-slate-700" />
          No pending approvals.
        </div>
      )}
    </div>
  )
}

// ── Audit Trail Tab ───────────────────────────────────────────────────────────

function AuditTab() {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useQuery({ queryKey: ['audit-log'], queryFn: () => auditLog(100) })

  const filtered = (data?.entries ?? []).filter((e: AuditEntry) =>
    !search ||
    e.actor.toLowerCase().includes(search.toLowerCase()) ||
    e.action.toLowerCase().includes(search.toLowerCase()) ||
    (e.resource_type ?? '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter by actor, action, or resource…"
          className="w-full pl-8 pr-3 py-2 text-sm bg-slate-800 border border-surface-border rounded-lg focus:outline-none focus:border-brand-500"
        />
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-slate-500 text-sm">Loading audit log…</div>
      ) : !filtered.length ? (
        <div className="py-8 text-center text-slate-500 text-sm">
          <FileText className="w-12 h-12 mx-auto mb-3 text-slate-700" />
          {search ? 'No entries match your search.' : 'Audit log is empty.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-surface-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/60 text-xs text-slate-500">
                <th className="text-left px-3 py-2.5">Actor</th>
                <th className="text-left px-3 py-2.5">Action</th>
                <th className="hidden sm:table-cell text-left px-3 py-2.5">Resource</th>
                <th className="text-right px-3 py-2.5 whitespace-nowrap">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {filtered.slice(0, 50).map((e: AuditEntry) => (
                <tr key={e.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-3 py-2.5 text-slate-300 font-mono text-xs max-w-[100px] truncate">{e.actor}</td>
                  <td className="px-3 py-2.5">
                    <Badge color={e.action.includes('delete') || e.action.includes('reject') ? 'red' : e.action.includes('approve') || e.action.includes('enable') ? 'green' : 'slate'}>
                      {e.action}
                    </Badge>
                  </td>
                  <td className="hidden sm:table-cell px-3 py-2.5 text-xs text-slate-500">
                    {e.resource_type && <span className="capitalize">{e.resource_type}</span>}
                    {e.resource_id && <span className="text-slate-600"> · {e.resource_id.slice(0, 8)}…</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-slate-600 whitespace-nowrap">
                    <span className="flex items-center justify-end gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(e.created_at).toLocaleString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Credential Vault Overview ─────────────────────────────────────────────────

function CredentialVaultTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['connected-accounts'],
    queryFn: listConnectedAccounts,
  })

  if (isLoading) return <div className="py-8 text-center text-slate-500 text-sm">Loading accounts…</div>

  const accounts = data?.accounts ?? []
  const connected = accounts.filter((a: ConnectedAccount) => a.status === 'connected').length

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-slate-800/40 rounded-xl p-3 sm:p-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center shrink-0">
            <Link className="w-4 h-4 text-slate-400" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-slate-500 truncate">Connected</div>
            <div className="text-xl font-bold text-slate-200">{data?.total ?? 0}</div>
          </div>
        </div>
        <div className="bg-slate-800/40 rounded-xl p-3 sm:p-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-green-900/30 flex items-center justify-center shrink-0">
            <CheckCircle className="w-4 h-4 text-green-400" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-slate-500 truncate">Active</div>
            <div className="text-xl font-bold text-green-400">{connected}</div>
          </div>
        </div>
      </div>

      {!accounts.length ? (
        /* Empty state — professional layout with visible icon + hierarchy + CTA */
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-surface-border flex items-center justify-center mb-4">
            <Link className="w-7 h-7 text-slate-400" />
          </div>
          <p className="text-sm font-semibold text-slate-200 mb-1">No connected accounts</p>
          <p className="text-xs text-slate-500 max-w-[220px] leading-relaxed mb-4">
            Connect your social and AI platforms to monitor credential health here.
          </p>
          <div className="flex items-center gap-1.5 text-xs text-brand-400 font-medium bg-brand-900/20 border border-brand-800/40 px-3 py-1.5 rounded-lg">
            <Shield className="w-3.5 h-3.5" />
            Settings → Connections
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map((a: ConnectedAccount) => (
            <div key={a.id} className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-xl border border-surface-border">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300 shrink-0">
                {a.platform.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-200 truncate">{a.display_name}</div>
                <div className="text-xs text-slate-500">{a.platform_label}</div>
              </div>
              <Badge color={a.status === 'connected' ? 'green' : 'red'}>{a.status}</Badge>
              <div className="hidden sm:block text-xs text-slate-600 shrink-0">
                {new Date(a.connected_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main GovernanceView ───────────────────────────────────────────────────────

const TABS: { id: Tab; label: string; icon: typeof Shield }[] = [
  { id: 'policies', label: 'Policies', icon: Shield },
  { id: 'approvals', label: 'Approvals', icon: CheckCircle },
  { id: 'audit', label: 'Audit Trail', icon: FileText },
  { id: 'credentials', label: 'Credential Health', icon: Link },
]

export default function GovernanceView() {
  const [tab, setTab] = useState<Tab>('policies')

  const { data: approvals } = useQuery({ queryKey: ['approvals'], queryFn: listApprovals })
  const pendingCount = (approvals?.approvals ?? []).filter((a: Approval) => a.status === 'pending').length

  return (
    <div>
      <PageHeader
        title="Governance & Compliance"
        subtitle="Policies · Approval queue · Audit trail · Credential health"
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Tab bar */}
        <div className="grid grid-cols-2 sm:flex gap-1 p-1 bg-slate-800/50 rounded-xl w-full sm:w-fit">
          {TABS.map(t => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center justify-center sm:justify-start gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  tab === t.id
                    ? 'bg-brand-700/50 text-brand-200'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{t.label}</span>
                {t.id === 'approvals' && pendingCount > 0 && (
                  <span className="bg-yellow-500 text-yellow-950 text-[11px] font-bold px-1.5 py-0.5 rounded-full leading-none shrink-0">
                    {pendingCount}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        <Card title={TABS.find(t => t.id === tab)?.label ?? ''}>
          {tab === 'policies' && <PoliciesTab />}
          {tab === 'approvals' && <ApprovalsTab />}
          {tab === 'audit' && <AuditTab />}
          {tab === 'credentials' && <CredentialVaultTab />}
        </Card>
      </div>
    </div>
  )
}
